import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import DatePickerField from "@/components/ui/date-picker-field";

const SettingsProfile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [userBirthday, setUserBirthday] = useState<Date | undefined>();
  const [savedBirthday, setSavedBirthday] = useState(false);
  const [initialFirstName, setInitialFirstName] = useState("");
  const [initialBirthday, setInitialBirthday] = useState<Date | undefined>();
  const [authProvider, setAuthProvider] = useState<string | null>(null);
  const [backupEmail, setBackupEmail] = useState("");
  const [initialBackupEmail, setInitialBackupEmail] = useState("");

  const hasChanges =
    firstName !== initialFirstName ||
    userBirthday?.getTime() !== initialBirthday?.getTime() ||
    backupEmail !== initialBackupEmail;

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("first_name, user_birthday, auth_provider, backup_email")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data) {
        const name = data.first_name ?? "";
        setFirstName(name);
        setInitialFirstName(name);
        if (data.user_birthday) {
          const bd = parseISO(data.user_birthday);
          setUserBirthday(bd);
          setInitialBirthday(bd);
          setSavedBirthday(true);
        }
        setAuthProvider((data as any).auth_provider ?? null);
        const be = (data as any).backup_email ?? "";
        setBackupEmail(be);
        setInitialBackupEmail(be);
      }
      setLoading(false);
    };
    load();
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    const updateData: Record<string, any> = {
      first_name: firstName.trim().slice(0, 100),
      user_birthday: userBirthday ? format(userBirthday, "yyyy-MM-dd") : null,
    };
    if (authProvider === "apple") {
      updateData.backup_email = backupEmail.trim() || null;
    }
    const { error } = await supabase
      .from("profiles")
      .update(updateData)
      .eq("user_id", user.id);
    setSaving(false);
    if (error) {
      toast.error("That didn't work — give it another go.");
    } else {
      setSavedBirthday(!!userBirthday);
      setInitialBirthday(userBirthday);
      setInitialBackupEmail(backupEmail.trim());
      toast.success("Profile saved.");
      navigate("/settings");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b border-border/50 bg-background/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 md:px-6 py-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/settings")} aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-page-title text-foreground">My Profile</h1>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-6 px-4 md:px-6 py-8 pb-24 animate-fade-in">
        <div className="card-premium p-6 space-y-6">
          {/* First name */}
          <div className="space-y-2">
            <Label htmlFor="firstName">First name</Label>
            <Input
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              maxLength={100}
              className="rounded-full"
              placeholder="Your first name"
            />
          </div>

          {/* Birthday — DateScrollPicker */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Label className="text-sm font-bold text-foreground">Your birthday</Label>
              {savedBirthday && (
                <Check className="h-3.5 w-3.5 text-accent" />
              )}
            </div>
            <p className="text-xs text-muted-foreground">So we can celebrate you on your special day 💛</p>
            <DatePickerField
              value={userBirthday}
              onChange={(date) => setUserBirthday(date)}
              placeholder="Add your birthday"
              title="Your birthday"
              yearOptional
            />
          </div>

          {/* Backup email — Apple auth only */}
          {authProvider === "apple" && (
            <div className="space-y-2">
              <Label htmlFor="backupEmail">Backup Email (optional)</Label>
              <Input
                id="backupEmail"
                type="email"
                value={backupEmail}
                onChange={(e) => setBackupEmail(e.target.value)}
                className="rounded-full"
                placeholder="your@email.com"
              />
              <p className="text-xs text-muted-foreground">
                In case you ever lose access to your Apple ID.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Fixed bottom save button */}
      <div className="fixed bottom-0 left-0 right-0 z-10">
        <div className="h-8 bg-gradient-to-t from-background to-transparent" />
        <div className="bg-background px-4 pb-6">
          <div className="mx-auto max-w-2xl">
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="w-full rounded-full"
              size="lg"
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsProfile;
